import { useCallback } from 'react';
import { Editor, Node, Element, Range, NodeEntry } from 'slate';
import { getDiffClass, getNodeId, leafsInRange, DiffEntry, DiffDecoration } from '../compareUtils';
import { CustomEditor } from 'slate/types/slate';

interface DecorateCompareOptions {
  side: 'left' | 'right';
  diffState: { left: Record<string, unknown>; right: Record<string, unknown> };
}

export const withDecorateCompare = (options: DecorateCompareOptions) => (editor: CustomEditor) => {
  const { side, diffState } = options;
  return useCallback(
    ([node, path]: NodeEntry) => {
      const ranges: DiffDecoration[] = [];
      const diffs = diffState[side];
      if (!diffs[getNodeId(node)]) {
        return ranges;
      }

      const changes = diffs[getNodeId(node)] as DiffEntry;

      if (changes.state !== 'changed') {
        return ranges;
      }
      switch (changes.type) {
        case 'table':
          changes.computedDiff
            ?.filter((change) => change[0] === 0 || change[0] === -1)
            .reduce<[number, DiffDecoration[]]>(
              (acc, change) => {
                if (change[0] === -1) {
                  const range = (Editor.range as (at: Node) => Range)(node);
                  acc[1].push({ ...range });
                }

                acc[0] += (change[1] ?? '').length;
                return acc;
              },
              [0, []]
            )[1]
            .forEach((decoration) => ranges.push(decoration));
          break;
        case 'numbered-list':
        case 'bulleted-list':
          changes.computedDiff?.forEach(([op, text], index) => {
            if (op === 0 || !Array.isArray([op, text])) return;

            const liPath = [...path, index];
            let liNode: Node;
            try {
              liNode = Node.get(editor, liPath);
            } catch {
              return;
            }

            if (!Element.isElement(liNode) || liNode.type !== 'list-item') return;

            const textNodes = Array.from(Node.texts(liNode));
            if (textNodes.length > 0) {
              textNodes.forEach(([textNode, textPath]) => {
                const len = textNode.text.length;
                ranges.push({
                  anchor: { path: [...liPath, ...textPath], offset: 0 },
                  focus: { path: [...liPath, ...textPath], offset: len },
                  highlight: getDiffClass(side),
                });
              });
            } else {
              ranges.push({
                anchor: { path: liPath, offset: 0 },
                focus: { path: liPath, offset: 0 },
                highlight: getDiffClass(side),
              });
            }
          });
          break;

        default:
          changes.computedDiff
            ?.filter((change) => change[0] === 0 || change[0] === -1)
            .reduce<[number, DiffDecoration[]]>(
              (acc, change) => {
                if (change[0] === -1) {
                  const lir = leafsInRange(path, node, acc[0], acc[0] + (change[1] ?? '').length, side, change);
                  acc[1].push(lir);
                }

                acc[0] += (change[1] ?? '').length;
                return acc;
              },
              [0, []]
            )[1]
            .forEach((decoration) => ranges.push(decoration));
      }

      return ranges;
    },
    [diffState, editor, side]
  );
};
